/* __next_internal_client_entry_do_not_use__ COMMON_TILE_IMAGE_DIMENSION,ProductImage auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AspectRatioPicture } from "@faire/retailer-visitor-shared/components/Image/AspectRatioPicture";
import { ProgressiveProductImage, ImageTransformationType } from "@faire/retailer-visitor-shared/components/Image/ProgressiveProductImage";
import { EAEAEA_PIXEL } from "@faire/retailer-visitor-shared/consts/EAEAEA_PIXEL";
import { ImageOverlay } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ImageOverlay";
import { ProductImageWithSrcsetSizes } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ProductImageWithSrcsetSizes";
import { VERTICAL_ASPECT_RATIO } from "@faire/retailer-visitor-shared/lib/verticalAspectRatio";
import { optimizeProductImageUrl, optimizeProductImageUrlFillVertical } from "@faire/web--source/common/images/optimizeImages";
import { MIN_WIDTH_BREAKPOINTS } from "@faire/web--source/common/media";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import isChromatic from "chromatic/isChromatic";
import { observer } from "mobx-react";
import * as React from "react";
var DEFAULT_IMAGE_SIZE = 350;
export var COMMON_TILE_IMAGE_DIMENSION = {
    mobileAndAbove: 370,
    tabletAndAbove: 350
};
var getImageSource = function(image, isSSR, dimension) {
    var showVerticalImagery = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : false;
    return showVerticalImagery ? optimizeProductImageUrlFillVertical(image, isSSR, dimension) : optimizeProductImageUrl(image, isSSR, dimension);
};
var useGetImageDimension = function(imageDimension) {
    var _useViewport = useViewport(), isXLargeAndAbove = _useViewport.isXLargeAndAbove, isDesktopAndAbove = _useViewport.isDesktopAndAbove, isTabletAndAbove = _useViewport.isTabletAndAbove, isMobile = _useViewport.isMobile;
    if (isXLargeAndAbove && (imageDimension === null || imageDimension === void 0 ? void 0 : imageDimension.xLargeAndAbove)) {
        return imageDimension === null || imageDimension === void 0 ? void 0 : imageDimension.xLargeAndAbove;
    }
    if (isDesktopAndAbove && (imageDimension === null || imageDimension === void 0 ? void 0 : imageDimension.desktopAndAbove)) {
        return imageDimension === null || imageDimension === void 0 ? void 0 : imageDimension.desktopAndAbove;
    }
    if (isTabletAndAbove && (imageDimension === null || imageDimension === void 0 ? void 0 : imageDimension.tabletAndAbove)) {
        return imageDimension === null || imageDimension === void 0 ? void 0 : imageDimension.tabletAndAbove;
    }
    if (isMobile && (imageDimension === null || imageDimension === void 0 ? void 0 : imageDimension.mobileAndAbove)) {
        return imageDimension === null || imageDimension === void 0 ? void 0 : imageDimension.mobileAndAbove;
    }
    return DEFAULT_IMAGE_SIZE;
};
var ProductImageBase = function(props) {
    var image = props.image, alt = props.alt, imageDimension = props.imageDimension, showVerticalImagery = props.showVerticalImagery, extraProductsCount = props.extraProductsCount, smallCount = props.smallCount, useLazyImageLoading = props.useLazyImageLoading, shouldIncrementallyLoadImages = props.shouldIncrementallyLoadImages, imageBreakpoints = props.imageBreakpoints, onLoad = props.onLoad;
    var isSSR = useIsSSR();
    var dataTestId = "product-image".concat(showVerticalImagery ? "-vertical" : "");
    var imageDimensionForViewport = useGetImageDimension(imageDimension);
    var transformation = React.useMemo(function() {
        return showVerticalImagery ? {
            type: ImageTransformationType.ProductFillVertical,
            width: imageDimensionForViewport
        } : {
            type: ImageTransformationType.Product,
            width: imageDimensionForViewport
        };
    }, [
        showVerticalImagery,
        imageDimensionForViewport
    ]);
    if (!(image === null || image === void 0 ? void 0 : image.url)) {
        return /*#__PURE__*/ _jsx(AspectRatioPicture, {
            $width: "100%",
            $ratio: showVerticalImagery ? VERTICAL_ASPECT_RATIO : 1,
            "data-test-id": dataTestId,
            $hideBorder: true,
            children: /*#__PURE__*/ _jsx("img", {
                alt: alt !== null && alt !== void 0 ? alt : image === null || image === void 0 ? void 0 : image.alt_text,
                src: EAEAEA_PIXEL,
                "data-product-image": "full",
                onLoad: onLoad
            })
        });
    }
    var getSource = function(minWidth, dimension) {
        return /*#__PURE__*/ _jsx("source", {
            srcSet: getImageSource(image, isSSR, dimension, showVerticalImagery),
            media: "(min-width: ".concat(minWidth, "px)")
        });
    };
    if (imageBreakpoints) {
        var _ref;
        return /*#__PURE__*/ _jsx(ProductImageWithSrcsetSizes, {
            image: image,
            alt: (_ref = alt !== null && alt !== void 0 ? alt : image.alt_text) !== null && _ref !== void 0 ? _ref : "",
            loading: useLazyImageLoading ? "lazy" : undefined,
            sizes: imageBreakpoints,
            showVerticalImagery: showVerticalImagery,
            extraProductsCount: extraProductsCount,
            smallCount: smallCount,
            onLoad: onLoad
        });
    }
    return /*#__PURE__*/ _jsxs(AspectRatioPicture, {
        $width: "100%",
        $ratio: showVerticalImagery ? VERTICAL_ASPECT_RATIO : 1,
        $hideBorder: true,
        "data-test-id": dataTestId,
        children: [
            shouldIncrementallyLoadImages && !isChromatic() ? /*#__PURE__*/ _jsx(ProgressiveProductImage, {
                image: image,
                alt: alt !== null && alt !== void 0 ? alt : image.alt_text,
                loading: useLazyImageLoading ? "lazy" : undefined,
                transformation: transformation,
                isSSR: isSSR,
                onLoad: onLoad
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    (imageDimension === null || imageDimension === void 0 ? void 0 : imageDimension.xLargeAndAbove) ? getSource(MIN_WIDTH_BREAKPOINTS.XLARGE, imageDimension.xLargeAndAbove) : null,
                    (imageDimension === null || imageDimension === void 0 ? void 0 : imageDimension.desktopAndAbove) ? getSource(MIN_WIDTH_BREAKPOINTS.DESKTOP, imageDimension.desktopAndAbove) : null,
                    (imageDimension === null || imageDimension === void 0 ? void 0 : imageDimension.tabletAndAbove) ? getSource(MIN_WIDTH_BREAKPOINTS.TABLET, imageDimension.tabletAndAbove) : null,
                    (imageDimension === null || imageDimension === void 0 ? void 0 : imageDimension.mobileAndAbove) ? getSource(MIN_WIDTH_BREAKPOINTS.MOBILE, imageDimension.mobileAndAbove) : null,
                    /*#__PURE__*/ _jsx("img", {
                        alt: alt !== null && alt !== void 0 ? alt : image.alt_text,
                        src: getImageSource(image, isSSR, DEFAULT_IMAGE_SIZE, showVerticalImagery),
                        loading: useLazyImageLoading ? "lazy" : undefined,
                        "data-product-image": "full",
                        onLoad: onLoad
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ImageOverlay, {
                extraProductsCount: extraProductsCount,
                smallCount: smallCount
            })
        ]
    });
};
export var ProductImage = observer(ProductImageBase);
